import { FC, memo, ReactElement } from 'react'
import { get } from 'es-toolkit/compat'
import useSelector from 'core/hooks/useSelector'
import { useApiGetStoresGroup } from 'core/api/stores-group/useApiGetStoresGroup'
import { StoresGroup } from 'core/models/stores-group'
import { useApiGetStores } from 'core/api/store/useApiGetStores'
import { useApiMenuGetUserPreferences } from 'core/api/menu/useApiMenuGetUserPreferences'
import { Combobox } from '../mygame/combobox/combobox'
import useObjFromList from 'core/hooks/useObjFromList'
import { processComboboxChange, SelectAllOption, selectAllOption } from 'core/utils/combobox'
import { useTranslation } from 'react-i18next'

export type SelectStoreProps = {
  id?: string
  storeId?: number | number[]
  className?: string
  label?: string
  value?: number | number[]
  favoriteStore?: number
  hasBottomLine?: boolean
  onChange?: (value: number | number[]) => void
  disableDefaultStore?: boolean
  isMultiple?: boolean
  isDisabled?: boolean
  disabledItemsIds?: number[]
  stores_group?: number
  canBeReset?: boolean
  isLoading?: boolean
}

export type Store = {
  id: number
  country_id?: string
  label: string
  store_forecast: string
  currency?: string
  opening_date?: Date | null
  is_open?: boolean
}

export type DumbSelectStoreProps = SelectStoreProps & {
  data?: Store[]
}

const sortStore = (a: Store, b: Store) => Number(a.id) - Number(b.id)

const addIdInLabel = (list: any) => {
  return list.map(option => ({
    ...option,
    label: `${option.id} - ${option.label}`
  }))
}

const disableListItem = (list: any, disabledItemsIds: number[]): any[] => {
  return list.map(item => ({
    ...item,
    isDisabled: disabledItemsIds?.includes(item.id) || false
  }))
}

export const DumbSelectStore: FC<DumbSelectStoreProps> = memo<DumbSelectStoreProps>(
  ({ label, isLoading, value, onChange, data = [], isMultiple, isDisabled, disabledItemsIds, canBeReset }) => {
    const { t } = useTranslation()
    const storesSorted: Store[] = addIdInLabel(data.sort(sortStore))
    const storesWithDisabledState: Store[] = disableListItem(storesSorted, disabledItemsIds)

    const optionsList: (SelectAllOption | Store)[] = isMultiple
      ? [selectAllOption(), ...storesWithDisabledState]
      : [...storesWithDisabledState]

    const objFromList = useObjFromList(storesWithDisabledState, 'id')
    const storeSelected: (SelectAllOption | Store)[] | Store =
      isMultiple && Array.isArray(value)
        ? optionsList.filter(store => value.includes(store.id))
        : objFromList[value as number]

    const placeholder = isLoading && !data.length ? t('loading') : t('settings.select_store')

    const handleChangeCombobox = (value, actionMeta) => {
      processComboboxChange(value, actionMeta, onChange, optionsList, disabledItemsIds)
    }

    return (
      <Combobox<Store | SelectAllOption>
        value={storeSelected}
        onChange={handleChangeCombobox}
        comboboxLabel={label || t('store')}
        options={optionsList}
        testId={'store'}
        isLoading={isLoading}
        isMulti={isMultiple}
        isDisabled={isDisabled}
        isClearable={canBeReset}
        placeholder={placeholder}
      />
    )
  }
)

export const getStoreListByStoreGroup = (storeGroups: StoresGroup[], storesGroupId: number, data: Store[]): Store[] => {
  if (!storeGroups || !storesGroupId || !data) return data
  const storeGroupSelect = storeGroups.find(store => store?.id === storesGroupId)
  const storesIdByStoreGroupSelected = storeGroupSelect?.composition
  return data.filter(storeElement => storesIdByStoreGroupSelected?.some(item => item?.store_id === storeElement?.id))
}

export const SelectStore = (props: SelectStoreProps): ReactElement => {
  const { data: user } = useApiMenuGetUserPreferences()
  const { data: stores, isLoading: loading } = useApiGetStores()
  const { data: storesGroup, isLoading: storesGroupLoading } = useApiGetStoresGroup()
  const value = useSelector(state => (props.storeId !== undefined ? props.storeId : get(state, 'filters.store_id')))
  const favoriteStore = user.preferences.default_filters.store_id

  const storesGroupId = props?.stores_group
  if (storesGroupId && storesGroupId !== -1) {
    return (
      <DumbSelectStore
        {...props}
        data={getStoreListByStoreGroup(storesGroup, storesGroupId, stores as Store[]) as Store[]}
        favoriteStore={favoriteStore}
        isLoading={loading || storesGroupLoading}
        disableDefaultStore={props?.disableDefaultStore ?? false}
      />
    )
  } else {
    return (
      <DumbSelectStore
        data={stores as Store[]}
        favoriteStore={favoriteStore}
        value={value}
        isLoading={loading}
        {...props}
      />
    )
  }
}

export default SelectStore
